/**
 * Workspace Survey Model Configuration
 * 
 * This file maps workspaces to their default survey models.
 * When a campaign is created, the system will automatically use
 * the correct survey model based on the workspace.
 */

// Survey IDs for each model
export const SURVEY_MODELS = {
  CARE: {
    mainSurveyId: '11111111-1111-1111-1111-111111111111',
    selfSurveyId: '11111111-1111-1111-1111-111111111112',
    name: 'CARE Framework',
    categories: ['CARE', 'COACH', 'DEVELOP', 'DELIVER', 'LEAD'],
  },
  LEADERSHIP: {
    mainSurveyId: 'fd48a0a2-d2aa-4c46-b837-a660930dfa74',
    selfSurveyId: 'b277ee1f-1542-4c53-8110-1790615af0ba',
    name: 'Leadership Framework',
    categories: ['VISION', 'COMMUNICATION', 'DEVELOPMENT', 'TRUST', 'PROBLEM_SOLVING'],
  },
} as const;

export type SurveyModelKey = keyof typeof SURVEY_MODELS;

// ============================================
// WORKSPACE-SPECIFIC SURVEY MODEL MAPPING
// ============================================

/**
 * Map workspace IDs to survey models
 * 
 * Add workspace IDs here to override the default model.
 * Workspaces not listed here will use the DEFAULT_SURVEY_MODEL.
 */
export const WORKSPACE_SURVEY_MODELS: Record<string, SurveyModelKey> = {
  // ShapeCorp uses Leadership model
  '74ed398a-5102-4d13-ba01-8c0dea134131': 'LEADERSHIP',
  
  // Add more workspace mappings here as needed
  // Example:
  // 'workspace-id-2': 'CARE',
  // 'workspace-id-3': 'LEADERSHIP',
};

/**
 * Default survey model for workspaces not explicitly mapped
 */
export const DEFAULT_SURVEY_MODEL: SurveyModelKey = 'CARE';

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get the survey model for a specific workspace
 * Returns the workspace-specific model or the default model
 */
export function getSurveyModelForWorkspace(workspaceId: string): SurveyModelKey {
  return WORKSPACE_SURVEY_MODELS[workspaceId] || DEFAULT_SURVEY_MODEL;
}

/**
 * Get the main survey ID for a workspace
 */
export function getSurveyIdForWorkspace(workspaceId: string): string {
  const model = getSurveyModelForWorkspace(workspaceId);
  return SURVEY_MODELS[model].mainSurveyId;
}

/**
 * Get the self-review survey ID for a workspace
 */
export function getSelfSurveyIdForWorkspace(workspaceId: string): string {
  const model = getSurveyModelForWorkspace(workspaceId);
  return SURVEY_MODELS[model].selfSurveyId;
}

/**
 * Get the full survey model configuration for a workspace
 */
export function getSurveyModelConfigForWorkspace(workspaceId: string) {
  const modelKey = getSurveyModelForWorkspace(workspaceId);
  return {
    modelKey,
    ...SURVEY_MODELS[modelKey],
  };
}

